import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { loanProductsApi, loanApplicationsApi, customerApi, guarantorApi } from '../../api';
import { LoanProduct, User, Guarantor } from '../../types';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorMessage } from '../../components/ErrorMessage';
import { CameraCapture } from '../../components/CameraCapture';
import { SignaturePanel } from '../../components/SignaturePanel';
import { DocumentPreview } from '../../components/DocumentPreview';
import { PersonalInfoStep } from '../../components/loan-application/PersonalInfoStep';
import { EmploymentInfoStep } from '../../components/loan-application/EmploymentInfoStep';
import { FinancialInfoStep } from '../../components/loan-application/FinancialInfoStep';
import { BankAccountStep } from '../../components/loan-application/BankAccountStep';
import { Button } from '../../components/Button';
import { Camera, Upload, FileText, X, CheckCircle, Users } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { CurrencyInput } from '../../components/CurrencyInput';
import { formatCurrency } from '../../utils/currency';

export const NewApplication: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const preselectedProduct = location.state?.product as LoanProduct | undefined;

  const [products, setProducts] = useState<LoanProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<LoanProduct | null>(
    preselectedProduct || null
  );

  // Multi-step form data
  const [personalInfo, setPersonalInfo] = useState<any>(null);
  const [employmentInfo, setEmploymentInfo] = useState<any>(null);
  const [financialInfo, setFinancialInfo] = useState<any>(null);
  const [bankAccountInfo, setBankAccountInfo] = useState<any>(null);

  const [loanDetailsData, setLoanDetailsData] = useState({
    product_id: preselectedProduct?.product_id || '',
    amount_requested: '',
    loan_term_months: '',
    purpose: '',
  });

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});
  const [calculation, setCalculation] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1);

  // Document upload state
  const [documentRequirements, setDocumentRequirements] = useState<any[]>([]);
  const [uploadedDocuments, setUploadedDocuments] = useState<Map<string, File>>(new Map());
  const [showCamera, setShowCamera] = useState(false);
  const [currentDocumentType, setCurrentDocumentType] = useState<string>('');

  // Signature state
  const [signature, setSignature] = useState<string>('');

  // Guarantor state
  const [guarantors, setGuarantors] = useState<Guarantor[]>([]);
  const [guarantorErrors, setGuarantorErrors] = useState<Record<number, Record<string, string>>>({});

  // Draft application ID (for backend persistence)
  const [draftApplicationId, setDraftApplicationId] = useState<string | null>(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await loanProductsApi.getAll();
        if (response.success && response.data) {
          setProducts(response.data.products);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load loan products');
      }
    };

    const loadDraftFromBackend = async () => {
      try {
        const response = await loanApplicationsApi.getDrafts();
        if (response.success && response.data?.drafts && response.data.drafts.length > 0) {
          const latestDraft = response.data.drafts[0]; // Get most recent draft
          setDraftApplicationId(latestDraft.application_id);

          // Load draft data if it exists
          if (latestDraft.draft_data) {
            const draftData = latestDraft.draft_data;
            if (draftData.personalInfo) setPersonalInfo(draftData.personalInfo);
            if (draftData.employmentInfo) setEmploymentInfo(draftData.employmentInfo);
            if (draftData.financialInfo) setFinancialInfo(draftData.financialInfo);
            if (draftData.bankAccountInfo) setBankAccountInfo(draftData.bankAccountInfo);
            if (draftData.loanDetailsData) setLoanDetailsData(draftData.loanDetailsData);
            if (draftData.signature) setSignature(draftData.signature);
            if (draftData.guarantors) setGuarantors(draftData.guarantors);
            if (draftData.step) setStep(draftData.step);
          }
        } else {
          // Fallback to localStorage if no backend draft
          const savedData = localStorage.getItem('loan_application_draft');
          if (savedData) {
            try {
              const parsed = JSON.parse(savedData);
              if (parsed.personalInfo) setPersonalInfo(parsed.personalInfo);
              if (parsed.employmentInfo) setEmploymentInfo(parsed.employmentInfo);
              if (parsed.financialInfo) setFinancialInfo(parsed.financialInfo);
              if (parsed.bankAccountInfo) setBankAccountInfo(parsed.bankAccountInfo);
              if (parsed.loanDetailsData) setLoanDetailsData(parsed.loanDetailsData);
              if (parsed.signature) setSignature(parsed.signature);
              if (parsed.guarantors) setGuarantors(parsed.guarantors);
              if (parsed.step) setStep(parsed.step);
            } catch (err) {
              // Ignore parse errors
            }
          }
        }
      } catch (err: any) {
        console.error('Failed to load draft from backend:', err);
        // Fallback to localStorage if backend fails
        const savedData = localStorage.getItem('loan_application_draft');
        if (savedData) {
          try {
            const parsed = JSON.parse(savedData);
            if (parsed.personalInfo) setPersonalInfo(parsed.personalInfo);
            if (parsed.employmentInfo) setEmploymentInfo(parsed.employmentInfo);
            if (parsed.financialInfo) setFinancialInfo(parsed.financialInfo);
            if (parsed.bankAccountInfo) setBankAccountInfo(parsed.bankAccountInfo);
            if (parsed.loanDetailsData) setLoanDetailsData(parsed.loanDetailsData);
            if (parsed.signature) setSignature(parsed.signature);
            if (parsed.guarantors) setGuarantors(parsed.guarantors);
            if (parsed.step) setStep(parsed.step);
          } catch (err) {
            // Ignore parse errors
          }
        }
      }
    };

    if (!preselectedProduct) {
      fetchProducts();
      loadDraftFromBackend();
    }
  }, [preselectedProduct]);

  // Auto-save form data to backend and localStorage
  useEffect(() => {
    const saveTimer = setTimeout(async () => {
      const draftData = {
        personalInfo,
        employmentInfo,
        financialInfo,
        bankAccountInfo,
        loanDetailsData,
        signature,
        guarantors,
        step,
      };

      // Always save to localStorage as backup
      localStorage.setItem('loan_application_draft', JSON.stringify(draftData));

      // Save to backend if we have form data
      if (personalInfo || employmentInfo || financialInfo || bankAccountInfo || loanDetailsData.product_id) {
        try {
          if (!draftApplicationId) {
            // Create new draft application
            if (loanDetailsData.product_id && loanDetailsData.amount_requested && loanDetailsData.loan_term_months) {
              const response = await loanApplicationsApi.create({
                product_id: loanDetailsData.product_id,
                amount_requested: parseFloat(loanDetailsData.amount_requested) || 0,
                loan_term_months: parseInt(loanDetailsData.loan_term_months) || 0,
                purpose: loanDetailsData.purpose,
              });

              if (response.success && response.data?.application) {
                const newDraftId = response.data.application.application_id;
                setDraftApplicationId(newDraftId);

                // Save draft data to newly created application
                await loanApplicationsApi.saveDraftData(newDraftId, draftData);
              }
            }
          } else {
            // Update existing draft
            await loanApplicationsApi.saveDraftData(draftApplicationId, draftData);
          }
        } catch (err: any) {
          console.error('Failed to save draft to backend:', err);
          // Don't show error to user - localStorage backup is sufficient
        }
      }
    }, 1000); // Debounce: save 1 second after last change

    return () => clearTimeout(saveTimer);
  }, [personalInfo, employmentInfo, financialInfo, bankAccountInfo, loanDetailsData, signature, guarantors, step, draftApplicationId]);

  // Step 1: Personal Information
  const handlePersonalInfoNext = (data: any) => {
    setPersonalInfo(data);
    setStep(2);
  };

  // Step 2: Employment Information
  const handleEmploymentInfoNext = (data: any) => {
    setEmploymentInfo(data);
    setStep(3);
  };

  // Step 3: Financial Information
  const handleFinancialInfoNext = (data: any) => {
    setFinancialInfo(data);
    setStep(4);
  };

  // Step 4: Bank Account Details
  const handleBankAccountNext = (data: any) => {
    setBankAccountInfo(data);
    setStep(5);
  };

  // Step 5: Loan Details
  const validateLoanField = (name: string, value: string): string => {
    switch (name) {
      case 'product_id':
        return !value ? 'Please select a loan product' : '';

      case 'amount_requested':
        if (!value) return 'Amount is required';
        const amount = parseFloat(value);
        if (isNaN(amount) || amount <= 0) return 'Amount must be greater than 0';
        if (selectedProduct) {
          const minAmount = Number(selectedProduct.min_amount);
          const maxAmount = Number(selectedProduct.max_amount);
          if (amount < minAmount) {
            return `Amount must be at least ₦${minAmount.toLocaleString()}`;
          }
          if (amount > maxAmount) {
            return `Amount cannot exceed ₦${maxAmount.toLocaleString()}`;
          }
        }
        return '';

      case 'loan_term_months':
        if (!value) return 'Loan term is required';
        const term = parseInt(value);
        if (isNaN(term) || term <= 0) return 'Loan term must be greater than 0';
        if (selectedProduct) {
          const minTerm = Number(selectedProduct.min_term_months);
          const maxTerm = Number(selectedProduct.max_term_months);
          if (term < minTerm) {
            return `Loan term must be at least ${minTerm} months`;
          }
          if (term > maxTerm) {
            return `Loan term cannot exceed ${maxTerm} months`;
          }
        }
        return '';

      default:
        return '';
    }
  };

  const handleLoanDetailsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setLoanDetailsData({
      ...loanDetailsData,
      [name]: value,
    });

    // Validate field if it's been touched
    if (touchedFields[name]) {
      const error = validateLoanField(name, value);
      setValidationErrors({ ...validationErrors, [name]: error });
    }
  };

  const handleProductChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const productId = e.target.value;
    const product = products.find((p) => p.product_id === productId);
    setSelectedProduct(product || null);
    setLoanDetailsData({ ...loanDetailsData, product_id: productId });

    // Validate product selection
    const error = validateLoanField('product_id', productId);
    setValidationErrors({ ...validationErrors, product_id: error });
    setTouchedFields({ ...touchedFields, product_id: true });

    // Fetch document requirements for selected product
    if (productId) {
      try {
        const response = await loanProductsApi.getDocumentRequirements(productId);
        if (response.success && response.data) {
          setDocumentRequirements(response.data.requirements);
        }
      } catch (err: any) {
        console.error('Failed to fetch document requirements:', err);
      }
    }
  };

  const handleLoanDetailsBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setTouchedFields({ ...touchedFields, [name]: true });
    const error = validateLoanField(name, value);
    setValidationErrors({ ...validationErrors, [name]: error });
  };

  const handleCalculate = async () => {
    // Validate all required fields
    const errors: Record<string, string> = {};
    errors.product_id = validateLoanField('product_id', loanDetailsData.product_id);
    errors.amount_requested = validateLoanField('amount_requested', loanDetailsData.amount_requested);
    errors.loan_term_months = validateLoanField('loan_term_months', loanDetailsData.loan_term_months);

    // Mark all fields as touched
    setTouchedFields({
      product_id: true,
      amount_requested: true,
      loan_term_months: true,
      purpose: true,
    });

    setValidationErrors(errors);

    // Check if there are any errors
    const hasErrors = Object.values(errors).some(error => error !== '');
    if (hasErrors) {
      setError('Please fix the validation errors before continuing');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await loanProductsApi.calculate(
        loanDetailsData.product_id,
        parseFloat(loanDetailsData.amount_requested),
        parseInt(loanDetailsData.loan_term_months)
      );
      if (response.success && response.data) {
        setCalculation(response.data.calculation);

        // Check if product requires guarantors
        if (selectedProduct?.required_guarantors_count && selectedProduct.required_guarantors_count > 0) {
          // Initialize guarantor array if needed
          if (guarantors.length === 0) {
            const initialGuarantors: Guarantor[] = Array(selectedProduct.required_guarantors_count)
              .fill(null)
              .map(() => ({ name: '', email: '', phone: '' }));
            setGuarantors(initialGuarantors);
          }
          setStep(6); // Move to guarantor step
        } else {
          setStep(7); // Skip guarantors, move to documents step
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to calculate loan');
    } finally {
      setLoading(false);
    }
  };

  // Step 6: Guarantor Information
  const validateGuarantorField = (index: number, field: string, value: string): string => {
    switch (field) {
      case 'name':
        if (!value.trim()) return 'Name is required';
        if (value.trim().length < 3) return 'Name must be at least 3 characters';
        return '';
      case 'email':
        if (!value.trim()) return 'Email is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return 'Invalid email format';
        return '';
      case 'phone':
        if (!value.trim()) return 'Phone number is required';
        const phoneRegex = /^(\+?234|0)[789]\d{9}$/;
        if (!phoneRegex.test(value.replace(/\s/g, ''))) {
          return 'Invalid Nigerian phone number (e.g., 08012345678)';
        }
        return '';
      default:
        return '';
    }
  };

  const handleGuarantorChange = (index: number, field: keyof Guarantor, value: string) => {
    const newGuarantors = [...guarantors];
    newGuarantors[index] = { ...newGuarantors[index], [field]: value };
    setGuarantors(newGuarantors);

    // Validate field
    const error = validateGuarantorField(index, field, value);
    setGuarantorErrors(prev => ({
      ...prev,
      [index]: {
        ...prev[index],
        [field]: error,
      },
    }));
  };

  const handleGuarantorsNext = () => {
    // Validate all guarantors
    const errors: Record<number, Record<string, string>> = {};
    let hasErrors = false;

    guarantors.forEach((guarantor, index) => {
      errors[index] = {
        name: validateGuarantorField(index, 'name', guarantor.name),
        email: validateGuarantorField(index, 'email', guarantor.email),
        phone: validateGuarantorField(index, 'phone', guarantor.phone),
      };

      if (Object.values(errors[index]).some(err => err !== '')) {
        hasErrors = true;
      }
    });

    setGuarantorErrors(errors);

    if (hasErrors) {
      setError('Please fix all guarantor information errors before continuing');
      return;
    }

    setError('');
    setStep(7); // Move to documents step
  };

  // Step 6: Document upload handlers
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>, docType: string) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError('');

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    // Add or replace document
    const newDocuments = new Map(uploadedDocuments);
    newDocuments.set(docType, file);
    setUploadedDocuments(newDocuments);
  };

  const handleCameraCapture = (docType: string) => {
    setCurrentDocumentType(docType);
    setShowCamera(true);
  };

  const handleCameraCaptured = (file: File) => {
    const newDocuments = new Map(uploadedDocuments);
    newDocuments.set(currentDocumentType, file);
    setUploadedDocuments(newDocuments);
    setShowCamera(false);
    setCurrentDocumentType('');
  };

  const handleRemoveDocument = (docType: string) => {
    const newDocuments = new Map(uploadedDocuments);
    newDocuments.delete(docType);
    setUploadedDocuments(newDocuments);
  };

  const handleDocumentsComplete = () => {
    // Validate all required documents are uploaded
    const requiredDocs = documentRequirements.filter(req => req.required);
    const missingDocs = requiredDocs.filter(req => !uploadedDocuments.has(req.documentType));

    if (missingDocs.length > 0) {
      const missingLabels = missingDocs.map(doc => doc.label).join(', ');
      setError(`Please upload required documents: ${missingLabels}`);
      return;
    }

    setError('');
    setStep(8); // Move to review step
  };

  // Step 7: Final submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate signature
    if (!signature) {
      setError('Please provide your signature before submitting the application');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // 0. Save all customer profile data first
      const profileData = {
        // Personal information
        first_name: personalInfo?.firstName,
        last_name: personalInfo?.lastName,
        middle_name: personalInfo?.middleName,
        date_of_birth: personalInfo?.dateOfBirth,
        gender: personalInfo?.gender,
        marital_status: personalInfo?.maritalStatus,

        // Address information
        address: personalInfo?.address,
        city: personalInfo?.city,
        state: personalInfo?.state,
        lga: personalInfo?.lga,

        // Employment information
        employment_status: employmentInfo?.employmentStatus,
        employer: employmentInfo?.employer,
        occupation: employmentInfo?.occupation,
        work_address: employmentInfo?.workAddress,
        employment_start_date: employmentInfo?.employmentStartDate,

        // Business information
        business_name: employmentInfo?.businessName,
        business_type: employmentInfo?.businessType,
        business_registration_number: employmentInfo?.businessRegistrationNumber,
        tax_identification_number: employmentInfo?.taxIdNumber,
        years_in_business: employmentInfo?.yearsInBusiness,

        // Financial information
        monthly_income: financialInfo?.monthlyIncome ? parseFloat(financialInfo.monthlyIncome) : undefined,
        monthly_expenses: financialInfo?.monthlyExpenses ? parseFloat(financialInfo.monthlyExpenses) : undefined,
        dependents: financialInfo?.dependents,

        // Bank information
        bank_name: bankAccountInfo?.bankName,
        account_number: bankAccountInfo?.accountNumber,
        account_name: bankAccountInfo?.accountName,
        bvn: bankAccountInfo?.bvn,

        // Next of kin information
        next_of_kin_name: personalInfo?.nextOfKinName,
        next_of_kin_phone: personalInfo?.nextOfKinPhone,
        next_of_kin_relationship: personalInfo?.nextOfKinRelationship,
      };

      await customerApi.updateProfile(profileData);

      // Prepare application data
      const applicationData = {
        product_id: loanDetailsData.product_id,
        amount_requested: parseFloat(loanDetailsData.amount_requested),
        loan_term_months: parseInt(loanDetailsData.loan_term_months),
        purpose: loanDetailsData.purpose,
        monthly_income: financialInfo?.monthlyIncome ? parseFloat(financialInfo.monthlyIncome) : undefined,
        existing_loans_count: financialInfo?.existingLoansCount || 0,
        existing_loans_total: financialInfo?.existingLoansTotal ? parseFloat(financialInfo.existingLoansTotal) : 0,
      };

      let applicationId: string;
      let applicationRef: string;

      // 1. Use existing draft or create new application
      if (draftApplicationId) {
        // Update existing draft with final data
        await loanApplicationsApi.update(draftApplicationId, applicationData);
        applicationId = draftApplicationId;

        // Get application reference
        const appResponse = await loanApplicationsApi.getById(draftApplicationId);
        if (appResponse.success && appResponse.data?.application) {
          applicationRef = appResponse.data.application.application_reference;
        } else {
          throw new Error('Failed to get application reference');
        }
      } else {
        // Create new application
        const response = await loanApplicationsApi.create(applicationData);
        if (response.success && response.data) {
          applicationId = response.data.application.application_id;
          applicationRef = response.data.application.application_reference;
        } else {
          throw new Error('Failed to create application');
        }
      }

      if (applicationId && applicationRef) {

        // 2. Upload all documents linked to the application
        if (uploadedDocuments.size > 0 || signature) {
          const formData = new FormData();

          const documentTypes: string[] = [];
          uploadedDocuments.forEach((file, docType) => {
            formData.append('files', file);
            documentTypes.push(docType);
          });

          // Add signature as a document
          if (signature) {
            // Convert base64 signature to File
            const base64Response = await fetch(signature);
            const blob = await base64Response.blob();
            const signatureFile = new File([blob], 'signature.png', { type: 'image/png' });

            formData.append('files', signatureFile);
            documentTypes.push('signature');
          }

          documentTypes.forEach(type => {
            formData.append('document_types', type);
          });

          formData.append('application_id', applicationId);

          try {
            await customerApi.uploadDocuments(formData);
          } catch (uploadErr: any) {
            console.error('Failed to upload documents:', uploadErr);
            setError('Application created but document upload failed. Please upload documents from your applications page.');
            return;
          }
        }

        // 3. Create guarantor requests if product requires guarantors
        if (selectedProduct?.required_guarantors_count && selectedProduct.required_guarantors_count > 0 && guarantors.length > 0) {
          try {
            await guarantorApi.createRequests(applicationId, guarantors);
          } catch (guarantorErr: any) {
            console.error('Failed to create guarantor requests:', guarantorErr);
            // Continue even if guarantor requests fail - admin can resend later
          }
        }

        // 4. Submit the application
        await loanApplicationsApi.submit(applicationId);

        // Clear saved draft
        localStorage.removeItem('loan_application_draft');

        navigate('/customer/applications', {
          state: {
            message: `Application submitted successfully! Your reference number is: ${applicationRef}`,
            reference: applicationRef
          }
        });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  const totalSteps = selectedProduct?.required_guarantors_count && selectedProduct.required_guarantors_count > 0 ? 8 : 7;
  const progressPercentage = Math.round((step / totalSteps) * 100);

  return (
    <div className="new-application-page">
      <div className="page-header">
        <h1>New Loan Application</h1>
        <p>Complete all steps to submit your loan application.</p>
      </div>

      {error && <ErrorMessage message={error} onClose={() => setError('')} />}

      <div className="application-progress">
        <div className="progress-header">
          <span className="progress-text">Step {step} of {totalSteps}</span>
          <span className="progress-percentage">{progressPercentage}% Complete</span>
        </div>
        <div className="progress-bar-container">
          <div className="progress-bar" style={{ width: `${progressPercentage}%` }}></div>
        </div>
        <div className="application-steps">
          <div className={`step ${step > 1 ? 'completed' : step === 1 ? 'active' : ''}`}>
            <div className="step-number">{step > 1 ? '✓' : '1'}</div>
            <div className="step-label">Personal Info</div>
            <div className="step-description">Basic details</div>
          </div>
          <div className="step-connector"></div>
          <div className={`step ${step > 2 ? 'completed' : step === 2 ? 'active' : ''}`}>
            <div className="step-number">{step > 2 ? '✓' : '2'}</div>
            <div className="step-label">Employment</div>
            <div className="step-description">Work details</div>
          </div>
          <div className="step-connector"></div>
          <div className={`step ${step > 3 ? 'completed' : step === 3 ? 'active' : ''}`}>
            <div className="step-number">{step > 3 ? '✓' : '3'}</div>
            <div className="step-label">Financial Info</div>
            <div className="step-description">Income & expenses</div>
          </div>
          <div className="step-connector"></div>
          <div className={`step ${step > 4 ? 'completed' : step === 4 ? 'active' : ''}`}>
            <div className="step-number">{step > 4 ? '✓' : '4'}</div>
            <div className="step-label">Bank Account</div>
            <div className="step-description">BVN verification</div>
          </div>
          <div className="step-connector"></div>
          <div className={`step ${step > 5 ? 'completed' : step === 5 ? 'active' : ''}`}>
            <div className="step-number">{step > 5 ? '✓' : '5'}</div>
            <div className="step-label">Loan Details</div>
            <div className="step-description">Amount & term</div>
          </div>
          {selectedProduct?.required_guarantors_count && selectedProduct.required_guarantors_count > 0 && (
            <>
              <div className="step-connector"></div>
              <div className={`step ${step > 6 ? 'completed' : step === 6 ? 'active' : ''}`}>
                <div className="step-number">{step > 6 ? '✓' : '6'}</div>
                <div className="step-label">Guarantors</div>
                <div className="step-description">Guarantor info</div>
              </div>
            </>
          )}
          <div className="step-connector"></div>
          <div className={`step ${step > (selectedProduct?.required_guarantors_count && selectedProduct.required_guarantors_count > 0 ? 7 : 6) ? 'completed' : step === (selectedProduct?.required_guarantors_count && selectedProduct.required_guarantors_count > 0 ? 7 : 6) ? 'active' : ''}`}>
            <div className="step-number">{step > (selectedProduct?.required_guarantors_count && selectedProduct.required_guarantors_count > 0 ? 7 : 6) ? '✓' : selectedProduct?.required_guarantors_count && selectedProduct.required_guarantors_count > 0 ? '7' : '6'}</div>
            <div className="step-label">Documents</div>
            <div className="step-description">Upload files</div>
          </div>
          <div className="step-connector"></div>
          <div className={`step ${step === (selectedProduct?.required_guarantors_count && selectedProduct.required_guarantors_count > 0 ? 8 : 7) ? 'active' : ''}`}>
            <div className="step-number">{selectedProduct?.required_guarantors_count && selectedProduct.required_guarantors_count > 0 ? '8' : '7'}</div>
            <div className="step-label">Review</div>
            <div className="step-description">Confirm & submit</div>
          </div>
        </div>
      </div>

      {/* Step 1: Personal Information */}
      {step === 1 && user && (
        <PersonalInfoStep
          user={user}
          initialData={personalInfo}
          onNext={handlePersonalInfoNext}
        />
      )}

      {/* Step 2: Employment Information */}
      {step === 2 && (
        <EmploymentInfoStep
          initialData={employmentInfo}
          onNext={handleEmploymentInfoNext}
          onBack={() => setStep(1)}
        />
      )}

      {/* Step 3: Financial Information */}
      {step === 3 && (
        <FinancialInfoStep
          initialData={financialInfo}
          employmentIncome={employmentInfo?.monthlySalary || employmentInfo?.monthlyBusinessRevenue}
          onNext={handleFinancialInfoNext}
          onBack={() => setStep(2)}
        />
      )}

      {/* Step 4: Bank Account Details */}
      {step === 4 && (
        <BankAccountStep
          initialData={bankAccountInfo}
          onNext={handleBankAccountNext}
          onBack={() => setStep(3)}
        />
      )}

      {/* Step 5: Loan Details */}
      {step === 5 && (
        <div className="application-form">
          <div className="form-section">
            <h3>Select Loan Product</h3>
            {!preselectedProduct ? (
              <div className="form-group">
                <label htmlFor="product_id">Loan Product *</label>
                <select
                  id="product_id"
                  name="product_id"
                  value={loanDetailsData.product_id}
                  onChange={handleProductChange}
                  required
                  disabled={loading}
                >
                  <option value="">Select a product</option>
                  {products.map((product) => (
                    <option key={product.product_id} value={product.product_id}>
                      {product.product_name}
                    </option>
                  ))}
                </select>
                {touchedFields.product_id && validationErrors.product_id && (
                  <span className="field-error">{validationErrors.product_id}</span>
                )}
              </div>
            ) : (
              <div className="selected-product">
                <h4>{selectedProduct?.product_name}</h4>
                <p>{selectedProduct?.description}</p>
              </div>
            )}
          </div>

          {selectedProduct && (
            <>
              <div className="form-section">
                <h3>Loan Amount & Term</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="amount_requested">Amount Requested *</label>
                    <CurrencyInput
                      id="amount_requested"
                      name="amount_requested"
                      value={loanDetailsData.amount_requested}
                      onChange={(value) => {
                        setLoanDetailsData({
                          ...loanDetailsData,
                          amount_requested: value.toString(),
                        });
                        if (touchedFields.amount_requested) {
                          const error = validateLoanField('amount_requested', value.toString());
                          setValidationErrors({ ...validationErrors, amount_requested: error });
                        }
                      }}
                      onBlur={(e) => {
                        setTouchedFields({ ...touchedFields, amount_requested: true });
                        const error = validateLoanField('amount_requested', loanDetailsData.amount_requested);
                        setValidationErrors({ ...validationErrors, amount_requested: error });
                      }}
                      min={selectedProduct?.min_amount ? Number(selectedProduct.min_amount) : undefined}
                      max={selectedProduct?.max_amount ? Number(selectedProduct.max_amount) : undefined}
                      required
                      disabled={loading}
                      placeholder={`Min: ${Number(selectedProduct?.min_amount || 0).toLocaleString()} - Max: ${Number(selectedProduct?.max_amount || 0).toLocaleString()}`}
                      error={touchedFields.amount_requested ? validationErrors.amount_requested : ''}
                      showLabel={false}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="loan_term_months">Loan Term (Months) *</label>
                    <input
                      type="number"
                      id="loan_term_months"
                      name="loan_term_months"
                      value={loanDetailsData.loan_term_months}
                      onChange={handleLoanDetailsChange}
                      onBlur={handleLoanDetailsBlur}
                      min={selectedProduct?.min_term_months}
                      max={selectedProduct?.max_term_months}
                      required
                      disabled={loading}
                      placeholder={`${selectedProduct?.min_term_months || 0} - ${selectedProduct?.max_term_months || 0} months`}
                      className={touchedFields.loan_term_months && validationErrors.loan_term_months ? 'error' : ''}
                    />
                    {touchedFields.loan_term_months && validationErrors.loan_term_months && (
                      <span className="field-error">{validationErrors.loan_term_months}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Loan Purpose</h3>
                <div className="form-group">
                  <label htmlFor="purpose">What will you use this loan for?</label>
                  <textarea
                    id="purpose"
                    name="purpose"
                    value={loanDetailsData.purpose}
                    onChange={handleLoanDetailsChange}
                    disabled={loading}
                    rows={4}
                    placeholder="Describe your loan purpose"
                  />
                </div>
              </div>

              <div className="form-actions">
                <Button type="button" onClick={() => setStep(4)} variant="secondary">
                  Back
                </Button>
                <Button
                  type="button"
                  onClick={handleCalculate}
                  variant="primary"
                  loading={loading}
                  disabled={loading}
                >
                  Calculate & Continue
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Step 6: Guarantor Information */}
      {step === 6 && selectedProduct?.required_guarantors_count && selectedProduct.required_guarantors_count > 0 && (
        <div className="application-form">
          <div className="form-section">
            <div className="guarantor-header">
              <Users size={32} className="guarantor-icon" />
              <h3>Guarantor Information</h3>
              <p className="section-description">
                This loan requires {selectedProduct.required_guarantors_count} guarantor{selectedProduct.required_guarantors_count > 1 ? 's' : ''}.
                Please provide their contact information. They will receive an email and SMS with a link to complete their guarantor authorization.
              </p>
            </div>

            {guarantors.map((guarantor, index) => (
              <div key={index} className="guarantor-form-section">
                <h4>Guarantor {index + 1} {selectedProduct.required_guarantors_count && index < selectedProduct.required_guarantors_count ? '*' : ''}</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor={`guarantor_${index}_name`}>Full Name *</label>
                    <input
                      type="text"
                      id={`guarantor_${index}_name`}
                      value={guarantor.name}
                      onChange={(e) => handleGuarantorChange(index, 'name', e.target.value)}
                      placeholder="Enter guarantor's full name"
                      className={guarantorErrors[index]?.name ? 'error' : ''}
                    />
                    {guarantorErrors[index]?.name && (
                      <span className="field-error">{guarantorErrors[index].name}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor={`guarantor_${index}_email`}>Email Address *</label>
                    <input
                      type="email"
                      id={`guarantor_${index}_email`}
                      value={guarantor.email}
                      onChange={(e) => handleGuarantorChange(index, 'email', e.target.value)}
                      placeholder="guarantor@example.com"
                      className={guarantorErrors[index]?.email ? 'error' : ''}
                    />
                    {guarantorErrors[index]?.email && (
                      <span className="field-error">{guarantorErrors[index].email}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor={`guarantor_${index}_phone`}>Phone Number *</label>
                    <input
                      type="tel"
                      id={`guarantor_${index}_phone`}
                      value={guarantor.phone}
                      onChange={(e) => handleGuarantorChange(index, 'phone', e.target.value)}
                      placeholder="08012345678"
                      className={guarantorErrors[index]?.phone ? 'error' : ''}
                    />
                    {guarantorErrors[index]?.phone && (
                      <span className="field-error">{guarantorErrors[index].phone}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}

            <div className="guarantor-info-box">
              <p><strong>What happens next?</strong></p>
              <ul>
                <li>After you submit this application, your guarantors will receive an email and SMS notification</li>
                <li>They will need to click the link and complete a short form with their details and documents</li>
                <li>Once all guarantors complete their authorization, your application will proceed to review</li>
                <li>The guarantor link will expire in 7 days</li>
              </ul>
            </div>
          </div>

          <div className="form-actions">
            <Button type="button" onClick={() => setStep(5)} variant="secondary">
              Back
            </Button>
            <Button
              type="button"
              onClick={handleGuarantorsNext}
              variant="primary"
            >
              Continue to Documents
            </Button>
          </div>
        </div>
      )}

      {/* Step 7 (or 6 if no guarantors): Document Upload */}
      {((step === 7 && selectedProduct?.required_guarantors_count && selectedProduct.required_guarantors_count > 0) ||
        (step === 6 && (!selectedProduct?.required_guarantors_count || selectedProduct.required_guarantors_count === 0))) && (
        <div className="application-form">
          <div className="form-section">
            <h3>Required Documents</h3>
            <p className="section-description">
              Please upload all required documents for your {selectedProduct?.product_name} application.
              You can use your camera to capture documents or upload files from your device.
            </p>

            {documentRequirements.length > 0 && (
              <div className="document-requirements-summary">
                <h4>You need to upload {documentRequirements.filter(req => req.required).length} required documents</h4>
                <ul>
                  {documentRequirements.filter(req => req.required).map(req => (
                    <li key={req.documentType}>{req.label}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="document-types-list">
            {documentRequirements.map((docReq) => {
              const uploadedDoc = uploadedDocuments.get(docReq.documentType);

              return (
                <div key={docReq.documentType} className="document-type-item">
                  <div className="document-type-info">
                    <div className="document-type-header">
                      <h4>
                        {docReq.label}
                        {docReq.required && <span className="required-badge">Required</span>}
                      </h4>
                    </div>
                    {docReq.description && (
                      <p className="document-description">{docReq.description}</p>
                    )}
                    {docReq.acceptedFormats && (
                      <small className="accepted-formats">
                        Accepted: {docReq.acceptedFormats.join(', ')}
                      </small>
                    )}
                  </div>

                  <div className="document-upload-actions">
                    {!uploadedDoc ? (
                      <>
                        <Button
                          type="button"
                          onClick={() => handleCameraCapture(docReq.documentType)}
                          variant="secondary"
                          size="sm"
                        >
                          <Camera size={18} />
                          <span>Capture</span>
                        </Button>
                        <label style={{ cursor: 'pointer', display: 'inline-block' }}>
                          <div className="button button--secondary button--sm">
                            <span className="button__icon button__icon--left"><Upload size={18} /></span>
                            <span className="button__text">Upload</span>
                          </div>
                          <input
                            type="file"
                            accept={docReq.acceptedFormats.join(',')}
                            onChange={(e) => handleFileSelect(e, docReq.documentType)}
                            style={{ display: 'none' }}
                          />
                        </label>
                      </>
                    ) : (
                      <div className="uploaded-file-info">
                        <div className="file-details">
                          <FileText size={18} />
                          <div className="file-meta">
                            <span className="file-name">{uploadedDoc.name}</span>
                            <span className="file-size">
                              {(uploadedDoc.size / (1024 * 1024)).toFixed(2)} MB
                            </span>
                          </div>
                          <CheckCircle size={18} className="success-icon" />
                        </div>
                        <button
                          className="btn-icon-only"
                          onClick={() => handleRemoveDocument(docReq.documentType)}
                          type="button"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="form-actions">
            <Button
              type="button"
              onClick={() => setStep(selectedProduct?.required_guarantors_count && selectedProduct.required_guarantors_count > 0 ? 6 : 5)}
              variant="secondary"
            >
              Back
            </Button>
            <Button
              type="button"
              onClick={handleDocumentsComplete}
              variant="primary"
              disabled={loading}
            >
              Continue to Review
            </Button>
          </div>
        </div>
      )}

      {/* Step 8 (or 7 if no guarantors): Review & Submit */}
      {((step === 8 && selectedProduct?.required_guarantors_count && selectedProduct.required_guarantors_count > 0) ||
        (step === 7 && (!selectedProduct?.required_guarantors_count || selectedProduct.required_guarantors_count === 0))) && calculation && (
        <form onSubmit={handleSubmit} className="application-form">
          <div className="form-section">
            <h3>Application Summary</h3>
            <p className="section-description">Please review your application details before submitting.</p>

            {/* Personal Information Summary */}
            <div className="summary-section">
              <h4>Personal Information</h4>
              <div className="summary-grid">
                <div><strong>Name:</strong> {personalInfo?.firstName} {personalInfo?.middleName} {personalInfo?.lastName}</div>
                <div><strong>Date of Birth:</strong> {personalInfo?.dateOfBirth}</div>
                <div><strong>Gender:</strong> {personalInfo?.gender}</div>
                <div><strong>Marital Status:</strong> {personalInfo?.maritalStatus}</div>
                <div><strong>Address:</strong> {personalInfo?.address}, {personalInfo?.city}, {personalInfo?.state}</div>
                <div><strong>Next of Kin:</strong> {personalInfo?.nextOfKinName} ({personalInfo?.nextOfKinRelationship})</div>
              </div>
            </div>

            {/* Employment Information Summary */}
            <div className="summary-section">
              <h4>Employment Information</h4>
              <div className="summary-grid">
                <div><strong>Status:</strong> {employmentInfo?.employmentStatus}</div>
                {employmentInfo?.employer && <div><strong>Employer:</strong> {employmentInfo.employer}</div>}
                {employmentInfo?.businessName && <div><strong>Business:</strong> {employmentInfo.businessName}</div>}
                {employmentInfo?.monthlySalary && <div><strong>Monthly Salary:</strong> {formatCurrency(employmentInfo.monthlySalary)}</div>}
                {employmentInfo?.monthlyBusinessRevenue && <div><strong>Monthly Revenue:</strong> {formatCurrency(employmentInfo.monthlyBusinessRevenue)}</div>}
              </div>
            </div>

            {/* Financial Information Summary */}
            <div className="summary-section">
              <h4>Financial Information</h4>
              <div className="summary-grid">
                <div><strong>Monthly Income:</strong> {formatCurrency(financialInfo?.monthlyIncome || 0)}</div>
                <div><strong>Monthly Expenses:</strong> {formatCurrency(financialInfo?.monthlyExpenses || 0)}</div>
                <div><strong>Dependents:</strong> {financialInfo?.dependents}</div>
                <div><strong>Existing Loans:</strong> {financialInfo?.existingLoansCount}</div>
              </div>
            </div>

            {/* Bank Account Summary */}
            <div className="summary-section">
              <h4>Bank Account Details</h4>
              <div className="summary-grid">
                <div><strong>Bank:</strong> {bankAccountInfo?.bankName}</div>
                <div><strong>Account Number:</strong> {bankAccountInfo?.accountNumber}</div>
                <div><strong>Account Name:</strong> {bankAccountInfo?.accountName}</div>
                <div><strong>BVN:</strong> {bankAccountInfo?.bvn?.replace(/\d(?=\d{4})/g, '*')}</div>
              </div>
            </div>

            {/* Loan Calculation Summary */}
            <h4>Loan Calculation</h4>
            <div className="calculation-details">
              <div className="calc-item">
                <span className="label">Principal Amount:</span>
                <span className="value">
                  {Number(calculation?.principal || 0).toLocaleString('en-NG', {
                    style: 'currency',
                    currency: 'NGN',
                  })}
                </span>
              </div>
              <div className="calc-item">
                <span className="label">Interest Rate:</span>
                <span className="value">{calculation?.interest_rate || 0}%</span>
              </div>
              <div className="calc-item">
                <span className="label">Term:</span>
                <span className="value">{calculation?.term_months || 0} months</span>
              </div>
              <div className="calc-item">
                <span className="label">Monthly Payment:</span>
                <span className="value">
                  {Number(calculation?.monthly_payment || 0).toLocaleString('en-NG', {
                    style: 'currency',
                    currency: 'NGN',
                  })}
                </span>
              </div>
              <div className="calc-item">
                <span className="label">Total Interest:</span>
                <span className="value">
                  {Number(calculation?.total_interest || 0).toLocaleString('en-NG', {
                    style: 'currency',
                    currency: 'NGN',
                  })}
                </span>
              </div>
              <div className="calc-item">
                <span className="label">Processing Fee:</span>
                <span className="value">
                  {Number(calculation?.processing_fee || 0).toLocaleString('en-NG', {
                    style: 'currency',
                    currency: 'NGN',
                  })}
                </span>
              </div>
              <div className="calc-item highlight">
                <span className="label">Total Repayment:</span>
                <span className="value">
                  {Number(calculation?.total_repayment || 0).toLocaleString('en-NG', {
                    style: 'currency',
                    currency: 'NGN',
                  })}
                </span>
              </div>
            </div>

            {/* Documents Summary with Thumbnails */}
            <div className="summary-section">
              <h4>Uploaded Documents</h4>
              <div className="documents-thumbnail-grid">
                {Array.from(uploadedDocuments.entries()).map(([docType, file]) => (
                  <DocumentPreview
                    key={docType}
                    file={file}
                    label={documentRequirements.find(r => r.documentType === docType)?.label || docType}
                  />
                ))}
              </div>
            </div>

            {/* Signature Panel */}
            <SignaturePanel
              onSave={(sig) => setSignature(sig)}
              initialSignature={signature}
              required={true}
            />
          </div>

          <div className="form-actions">
            <Button
              type="button"
              onClick={() => setStep(selectedProduct?.required_guarantors_count && selectedProduct.required_guarantors_count > 0 ? 7 : 6)}
              variant="secondary"
            >
              Back
            </Button>
            <Button type="submit" variant="primary" loading={loading} disabled={loading}>
              Submit Application
            </Button>
          </div>
        </form>
      )}

      {/* Camera Capture Modal */}
      {showCamera && (
        <CameraCapture
          onCapture={handleCameraCaptured}
          onCancel={() => {
            setShowCamera(false);
            setCurrentDocumentType('');
          }}
          documentLabel={
            documentRequirements.find(req => req.documentType === currentDocumentType)?.label || 'Document'
          }
        />
      )}

      <style>{`
        /* Guarantor Section Styles */
        .guarantor-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .guarantor-icon {
          color: var(--primary-color);
          margin-bottom: 1rem;
        }

        .guarantor-form-section {
          background: var(--gray-50);
          border: 1px solid var(--gray-200);
          border-radius: 8px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
        }

        .guarantor-form-section h4 {
          margin-bottom: 1rem;
          color: var(--gray-800);
          font-size: 1.125rem;
        }

        .guarantor-info-box {
          background: var(--color-info-light, #e3f2fd);
          border-left: 4px solid var(--color-info, #2196f3);
          padding: 1.5rem;
          margin-top: 2rem;
          border-radius: 4px;
        }

        .guarantor-info-box p {
          margin-bottom: 0.75rem;
          color: var(--gray-800);
        }

        .guarantor-info-box ul {
          list-style: disc;
          padding-left: 1.5rem;
          margin: 0;
        }

        .guarantor-info-box li {
          margin-bottom: 0.5rem;
          color: var(--gray-700);
        }

        /* Documents Thumbnail Grid */
        .documents-thumbnail-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 1rem;
          margin-top: 1rem;
        }

        /* Tablet Responsive Styles */
        @media (max-width: 1024px) {
          .application-steps {
            flex-wrap: wrap;
            justify-content: center;
          }

          .step {
            min-width: 120px;
          }

          .step-connector {
            display: none;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .summary-grid {
            grid-template-columns: 1fr;
          }

          .document-requirements-summary {
            padding: 1.25rem;
          }
        }

        /* Mobile Responsive Styles */
        @media (max-width: 768px) {
          .new-application-page {
            padding: 1rem;
          }

          .page-header h1 {
            font-size: 1.75rem;
          }

          .page-header p {
            font-size: 0.9375rem;
          }

          .application-progress {
            margin-bottom: 1.5rem;
          }

          .progress-header {
            font-size: 0.875rem;
          }

          .application-steps {
            flex-direction: column;
            gap: 0.75rem;
          }

          .step {
            flex-direction: row;
            padding: 0.75rem 1rem;
            align-items: center;
            gap: 0.75rem;
          }

          .step-number {
            width: 2rem;
            height: 2rem;
            font-size: 0.875rem;
          }

          .step-label {
            font-size: 0.9375rem;
          }

          .step-description {
            display: none;
          }

          .application-form {
            padding: 1.25rem;
          }

          .form-section h3 {
            font-size: 1.125rem;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .form-group label {
            font-size: 0.875rem;
          }

          .form-group input,
          .form-group select,
          .form-group textarea {
            padding: 0.875rem;
            font-size: 1rem;
          }

          .form-actions {
            flex-direction: column;
            gap: 0.75rem;
          }

          .form-actions button {
            width: 100%;
          }

          .selected-product {
            padding: 1.25rem;
          }

          .selected-product h4 {
            font-size: 1.125rem;
          }

          .calculation-details {
            gap: 0.75rem;
          }

          .calc-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.25rem;
          }

          .document-type-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
          }

          .document-upload-actions {
            width: 100%;
            display: flex;
            gap: 0.5rem;
          }

          .document-upload-actions button,
          .document-upload-actions label {
            flex: 1;
          }

          .uploaded-file-info {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75rem;
          }

          .summary-section {
            padding: 1.25rem;
          }

          .summary-section h4 {
            font-size: 1.0625rem;
          }

          .summary-grid {
            grid-template-columns: 1fr;
            gap: 0.75rem;
            font-size: 0.875rem;
          }

          .documents-summary {
            flex-direction: column;
            gap: 0.5rem;
          }

          .documents-thumbnail-grid {
            grid-template-columns: 1fr;
          }
        }

        /* Small Mobile Responsive Styles */
        @media (max-width: 480px) {
          .new-application-page {
            padding: 0.875rem;
          }

          .page-header h1 {
            font-size: 1.5rem;
          }

          .application-progress {
            margin-bottom: 1.5rem;
          }

          .progress-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.25rem;
            margin-bottom: 0.75rem;
          }

          .progress-text {
            font-size: 1rem;
            font-weight: 600;
            color: var(--color-primary-600);
          }

          .progress-percentage {
            font-size: 0.8125rem;
            color: var(--color-text-secondary);
          }

          .progress-bar-container {
            height: 8px;
            margin-bottom: 1rem;
          }

          .progress-bar {
            border-radius: 4px;
          }

          /* Compact horizontal step dots for mobile */
          .application-steps {
            display: flex;
            flex-direction: row !important;
            justify-content: space-between;
            align-items: center;
            gap: 0.5rem;
            overflow-x: auto;
            padding: 0.5rem 0;
          }

          .step {
            flex-direction: column;
            align-items: center;
            min-width: auto;
            padding: 0;
            background: transparent;
            border: none;
            box-shadow: none;
          }

          .step-number {
            width: 32px;
            height: 32px;
            font-size: 0.875rem;
            margin-bottom: 0.25rem;
          }

          .step-label {
            font-size: 0.625rem;
            text-align: center;
            max-width: 60px;
            line-height: 1.2;
            color: var(--color-text-tertiary);
          }

          .step.active .step-label {
            color: var(--color-primary-600);
            font-weight: 600;
          }

          .step.completed .step-label {
            color: var(--color-success);
          }

          .step-description {
            display: none !important;
          }

          .step-connector {
            flex: 1;
            height: 2px;
            min-width: 8px;
            max-width: 24px;
            margin: 0;
          }

          .application-form {
            padding: 1rem;
          }

          .form-section {
            padding: 1rem;
          }

          .form-group input,
          .form-group select,
          .form-group textarea {
            padding: 0.75rem;
            font-size: 0.9375rem;
          }

          .selected-product {
            padding: 1rem;
          }

          .calc-item {
            font-size: 0.875rem;
          }

          .calc-item.highlight {
            padding: 0.875rem;
          }

          .document-type-item {
            padding: 1rem;
          }

          .document-type-header h4 {
            font-size: 0.9375rem;
          }

          .summary-section {
            padding: 1rem;
          }

          .summary-grid {
            font-size: 0.8125rem;
          }
        }
      `}</style>
    </div>
  );
};
